// ============================================
// INFRASTRUCTURE - Fiados Repository
// ============================================

import { axiosClient } from './axios-client';
import type {
  FiadosPendientesResponse,
  FiadosHistoricoResponse,
  FiltrosFiadosPendientes,
  FiltrosFiadosHistorico,
  PagarFiadoRequest,
  DetalleFiadosClienteResponse,
  DetalleFiadoCliente,
  ReasignarFiadoRequest,
  FiadoTotalesResponse,
  Abono
} from '../../domain/dtos';

export const fiadosRepository = {
  /**
   * Obtener fiados pendientes con filtros opcionales
   * GET /fiados/pendientesByFilters
   */
  async obtenerFiadosPendientes(filtros?: FiltrosFiadosPendientes): Promise<FiadosPendientesResponse> {
    const params = new URLSearchParams();
    
    if (filtros?.nombreCliente && filtros.nombreCliente.length >= 3) {
      params.append('nombreCliente', filtros.nombreCliente);
    }
    if (filtros?.rutDv) {
      params.append('rutDv', filtros.rutDv);
    }
    if (filtros?.page !== undefined) {
      params.append('page', filtros.page.toString());
    }
    if (filtros?.sort) {
      params.append('sort', filtros.sort);
    }

    const queryString = params.toString();
    const url = queryString
      ? `/fiados/pendientesByFilters?${queryString}`
      : '/fiados/pendientesByFilters';
    
    const { data } = await axiosClient.get<FiadosPendientesResponse>(url);
    return data;
  },

  /**
   * Obtener histórico de fiados con filtros
   * GET /fiados/filters
   */
  async obtenerFiadosHistorico(filtros: FiltrosFiadosHistorico): Promise<FiadosHistoricoResponse> {
    const params = new URLSearchParams();
    
    if (filtros.nombreCliente && filtros.nombreCliente.length >= 3) {
      params.append('nombreCliente', filtros.nombreCliente);
    }
    if (filtros.pagado !== undefined) {
      params.append('pagado', filtros.pagado.toString());
    }
    // Fechas obligatorias
    params.append('fechaDesde', filtros.fechaDesde);
    params.append('fechaHasta', filtros.fechaHasta);
    
    if (filtros.page !== undefined) {
      params.append('page', filtros.page.toString());
    }

    const queryString = params.toString();
    const url = `/fiados/filters?${queryString}`;
    
    const { data } = await axiosClient.get<FiadosHistoricoResponse>(url);
    return data;
  },

  /**
   * Eliminar un fiado
   * DELETE /fiados/delete/{fiadoId}
   */
  async eliminarFiado(fiadoId: number): Promise<void> {
    await axiosClient.delete(`/fiados/delete/${fiadoId}`);
  },

  /**
   * Pagar uno o varios fiados
   * POST /fiados/pagar
   */
  async pagarFiados(request: PagarFiadoRequest): Promise<void> {
    await axiosClient.post('/fiados/pagar', request);
  },

  /**
   * Obtener detalle de fiados de un cliente
   * GET /fiados/cliente_almacen/{clienteAlmacenId}
   */
  async obtenerDetalleFiadosCliente(clienteAlmacenId: number, page: number = 0): Promise<DetalleFiadosClienteResponse> {
    const { data } = await axiosClient.get<DetalleFiadosClienteResponse>(
      `/fiados/cliente_almacen/${clienteAlmacenId}?page=${page}`
    );
    return data;
  },

  /**
   * Reasignar un fiado a otro cliente del mismo almacén
   * PATCH /fiados/{fiadoId}/cliente
   */
  async reasignarFiado(fiadoId: number, request: ReasignarFiadoRequest): Promise<DetalleFiadoCliente> {
    const { data } = await axiosClient.patch<DetalleFiadoCliente>(`/fiados/${fiadoId}/cliente`, request);
    return data;
  },

  /**
   * Obtener totales globales de fiados pendientes de un cliente
   * GET /fiados/cliente_almacen/{clienteAlmacenId}/totales
   */
  async obtenerTotalesFiadosCliente(clienteAlmacenId: number): Promise<FiadoTotalesResponse> {
    const { data } = await axiosClient.get<FiadoTotalesResponse>(
      `/fiados/cliente_almacen/${clienteAlmacenId}/totales`
    );
    return data;
  },

  /**
   * Obtener historial de abonos de un fiado
   * GET /fiados/{fiadoId}/abonos
   */
  async obtenerAbonosFiado(fiadoId: number): Promise<Abono[]> {
    const { data } = await axiosClient.get<Abono[]>(`/fiados/${fiadoId}/abonos`);
    return data;
  },

  /**
   * Pagar todos los fiados pendientes de un cliente en una sola acción
   * POST /fiados/cliente_almacen/{clienteAlmacenId}/pagar-todo
   */
  async pagarTodoFiadosCliente(clienteAlmacenId: number, tipoPago: string): Promise<void> {
    await axiosClient.post(`/fiados/cliente_almacen/${clienteAlmacenId}/pagar-todo`, { tipoPago });
  },
};
